import { pickClass, setActiveClass } from "@/lib/activeClass";
import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, MessageCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

export default function Absences({ classId: forcedClassId }) {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [ownClassId, setClassId] = useState("");
  const classId = forcedClassId || ownClassId;
  const [students, setStudents] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", date: new Date().toISOString().slice(0, 10), demi_journee: "matin", motif: "", justifiee: false });

  useEffect(() => { api.get("/classes").then(({ data }) => { setClasses(data); if (data.length > 0) setClassId(pickClass(data)); }); }, []);

  const load = async () => {
    if (!classId) return;
    try {
      const [s, a] = await Promise.all([
        api.get("/students", { params: { class_id: classId } }),
        api.get("/absences", { params: { class_id: classId } }),
      ]);
      const sorted = [...s.data].sort((a, b) =>
        (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" })
        || (a.prenom || "").localeCompare(b.prenom || "", "fr", { sensitivity: "base" })
      );
      setStudents(sorted); setAbsences(a.data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, [classId]);

  const create = async () => {
    try {
      await api.post("/absences", form);
      toast.success("Absence enregistrée");
      setOpen(false);
      setForm({ ...form, motif: "", justifiee: false });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (a) => {
    if (!confirm("Supprimer cette absence ?")) return;
    try { await api.delete(`/absences/${a.id}`); load(); } catch (e) { toast.error(formatError(e)); }
  };

  const studentOf = (id) => students.find((s) => s.id === id);
  const waLink = (a) => {
    const st = studentOf(a.student_id);
    if (!st?.telephone_parent) return null;
    const tel = String(st.telephone_parent).replace(/\D/g, "");
    const msg = encodeURIComponent(`Bonjour, votre enfant ${st.prenom} ${st.nom} a été absent(e) le ${a.date} (${a.demi_journee}).`);
    return `https://wa.me/${tel.startsWith("221") ? tel : "221" + tel}?text=${msg}`;
  };

  const canEdit = user.role === "maitre";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-bold" style={{ fontFamily: "Outfit" }}>Absences</h2>
          <p className="text-sm text-[#5C736A]">{absences.length} absence(s) enregistrée(s)</p>
        </div>
        <div className="flex gap-2">
          {!forcedClassId && (
            <Select value={classId} onValueChange={(v) => { setClassId(v); setActiveClass(v); }}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {canEdit && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="add-absence-btn">
                  <Plus className="w-4 h-4 mr-1" /> Nouvelle absence
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Saisir une absence</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Élève</Label>
                    <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Choisir un élève" /></SelectTrigger>
                      <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                  <div>
                    <Label>Demi-journée</Label>
                    <Select value={form.demi_journee} onValueChange={(v) => setForm({ ...form, demi_journee: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="matin">Matin</SelectItem>
                        <SelectItem value="apres-midi">Après-midi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Motif (optionnel)</Label><Input value={form.motif} onChange={(e) => setForm({ ...form, motif: e.target.value })} /></div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="just" checked={form.justifiee} onCheckedChange={(v) => setForm({ ...form, justifiee: !!v })} />
                    <Label htmlFor="just" className="cursor-pointer">Absence justifiée</Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button onClick={create} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white">Enregistrer</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="ek-card p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Élève</TableHead>
              <TableHead>Demi-journée</TableHead>
              <TableHead>Motif</TableHead>
              <TableHead>Justifiée ?</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {absences.map((a) => {
              const st = studentOf(a.student_id);
              return (
                <TableRow key={a.id}>
                  <TableCell>{a.date}</TableCell>
                  <TableCell>{st ? `${st.prenom} ${st.nom}` : "—"}</TableCell>
                  <TableCell className="capitalize">{a.demi_journee}</TableCell>
                  <TableCell>{a.motif || "—"}</TableCell>
                  <TableCell>{a.justifiee ? "Oui" : "Non"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {waLink(a) && (
                        <a href={waLink(a)} target="_blank" rel="noreferrer">
                          <Button size="icon" variant="ghost"><MessageCircle className="w-4 h-4 text-green-600" /></Button>
                        </a>
                      )}
                      {canEdit && <Button size="icon" variant="ghost" onClick={() => del(a)}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {absences.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#5C736A] py-8">Aucune absence enregistrée.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
